import React from 'react';
import { XMarkIcon, UserPlusIcon } from '@heroicons/react/24/outline';
import { PHONE_PLACEHOLDER } from '../../config/region';
import type { Run } from './leadsTypes';
import type { AddLeadFormState } from './AddLeadForm';

type AddLeadModalProps = {
  isOpen: boolean;
  onClose: () => void;
  form: AddLeadFormState;
  setForm: (f: AddLeadFormState | ((prev: AddLeadFormState) => AddLeadFormState)) => void;
  onSubmit: (e: React.FormEvent) => void;
  submitting: boolean;
  runs: Map<string, Run>;
};

const AddLeadModal: React.FC<AddLeadModalProps> = ({
  isOpen,
  onClose,
  form,
  setForm,
  onSubmit,
  submitting,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed top-16 left-0 right-0 bottom-0 lg:left-56 z-50 overflow-y-auto custom-scrollbar flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" aria-hidden="true" onClick={onClose} />
      <div className="relative z-10 w-full max-w-md rounded-xl bg-slate-800 border border-slate-600/50 shadow-xl">
          <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-slate-700/50">
            <div className="flex items-center gap-2">
              <UserPlusIcon className="w-6 h-6 text-cyan-400" />
              <h2 className="text-lg font-semibold text-white">Add Lead</h2>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="p-1.5 text-gray-400 hover:text-white rounded-lg hover:bg-white/10 transition-colors"
              aria-label="Close"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
          <form onSubmit={onSubmit} className="p-4 sm:p-6 space-y-4">
            <div>
              <label htmlFor="add-lead-name" className="block text-sm font-medium text-gray-300 mb-1">Name *</label>
              <input
                id="add-lead-name"
                type="text"
                required
                value={form.name}
                onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                placeholder="e.g. John Doe"
                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
              />
            </div>
            <div>
              <label htmlFor="add-lead-phone" className="block text-sm font-medium text-gray-300 mb-1">Phone *</label>
              <input
                id="add-lead-phone"
                type="tel"
                required
                value={form.phone}
                onChange={(e) => setForm((prev) => ({ ...prev, phone: e.target.value }))}
                placeholder={`e.g. ${PHONE_PLACEHOLDER}`}
                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
              />
            </div>
            <div>
              <label htmlFor="add-lead-email" className="block text-sm font-medium text-gray-300 mb-1">Email (optional)</label>
              <input
                id="add-lead-email"
                type="email"
                value={form.email}
                onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
                placeholder="e.g. john@example.com"
                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
              />
            </div>
            <div className="flex gap-3 pt-2">
              <button type="button" onClick={onClose} className="flex-1 px-4 py-2 bg-white/10 text-gray-300 rounded-lg hover:bg-white/15 transition-colors">Cancel</button>
              <button type="submit" disabled={submitting} className="flex-1 px-4 py-2 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-lg hover:from-emerald-600 hover:to-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                {submitting ? 'Adding...' : 'Add Lead'}
              </button>
            </div>
          </form>
      </div>
    </div>
  );
};

export default AddLeadModal;
